import type {
    Field,
    FieldOption,
    FieldType,
    Step,
} from '../types/generator-config';
import type { IconName } from '../components/Icon';

/**
 * Génère un ID court et unique. `crypto.randomUUID()` est dispo partout
 * dans les navigateurs modernes + Node récents.
 */
export function randomId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        return crypto.randomUUID();
    }
    // Fallback (très peu probable d'y tomber en prod)
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/** Étape vide, prête à recevoir des champs */
export function createStep(partial?: Partial<Step>): Step {
    return {
        id: randomId(),
        title: partial?.title ?? 'Nouvelle étape',
        description: partial?.description,
        fields: partial?.fields ?? [],
    };
}

/** Option par défaut pour les fields à choix */
export function createOption(partial?: Partial<FieldOption>): FieldOption {
    return {
        id: randomId(),
        label: partial?.label ?? 'Nouvelle option',
        icon: partial?.icon,
        helper: partial?.helper,
    };
}

/** Crée un field avec des valeurs raisonnables selon son type */
export function createField(type: FieldType): Field {
    const id = randomId();
    const label = defaultLabelForType(type);

    switch (type) {
        case 'select':
            return {
                id,
                type,
                label,
                required: false,
                options: [createOption({ label: 'Option 1' }), createOption({ label: 'Option 2' })],
            };
        case 'chips':
            return {
                id,
                type,
                label,
                required: false,
                multi: false,
                options: [createOption({ label: 'Choix 1' }), createOption({ label: 'Choix 2' })],
            };
        case 'slider':
            return {
                id,
                type,
                label,
                required: false,
                min: 0,
                max: 100,
                step: 1,
                defaultValue: 50,
            };
        case 'number':
            return {
                id,
                type,
                label,
                required: false,
                min: 0,
            };
        case 'radio':
            return {
                id,
                type,
                label,
                required: false,
                options: [createOption({ label: 'Option 1' }), createOption({ label: 'Option 2' })],
            };
        case 'textarea':
            return {
                id,
                type,
                label,
                required: false,
            };
        case 'checkbox-group':
            return {
                id,
                type,
                label,
                required: false,
                options: [createOption({ label: 'Option 1' }), createOption({ label: 'Option 2' })],
            };
    }
}

/** Libellé humain du type de champ (pour menus) */
export function labelForType(type: FieldType): string {
    const map: Record<FieldType, string> = {
        select: 'Liste déroulante',
        chips: 'Chips (boutons)',
        slider: 'Curseur',
        number: 'Nombre',
        radio: 'Choix unique (radio)',
        textarea: 'Texte long',
        'checkbox-group': 'Cases à cocher',
    };
    return map[type];
}

/** Icône SVG associée à un type de champ (pour menus & listes) */
export function iconForType(type: FieldType): IconName {
    const map: Record<FieldType, IconName> = {
        select: 'list',
        chips: 'chips',
        slider: 'slider',
        number: 'number',
        radio: 'radio',
        textarea: 'textarea',
        'checkbox-group': 'checkbox',
    };
    return map[type];
}

function defaultLabelForType(type: FieldType): string {
    const map: Record<FieldType, string> = {
        select: 'Nouvelle liste',
        chips: 'Nouveau choix',
        slider: 'Nouvelle valeur',
        number: 'Nouveau nombre',
        radio: 'Nouvelle question',
        textarea: 'Nouveau texte',
        'checkbox-group': 'Nouvelles options',
    };
    return map[type];
}

/** Le type a-t-il une liste d'options modifiable ? */
export function fieldHasOptions(
    field: Field
): field is Extract<Field, { options: FieldOption[] }> {
    return (
        field.type === 'select' ||
        field.type === 'chips' ||
        field.type === 'radio' ||
        field.type === 'checkbox-group'
    );
}

export const ALL_FIELD_TYPES: FieldType[] = [
    'select',
    'chips',
    'radio',
    'slider',
    'number',
    'textarea',
    'checkbox-group',
];
